import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Svg,
    Circle,
    Line,
    Ellipse,
    pdf,
} from "@react-pdf/renderer";
import {
    HEADER,
    FIRM_BACKGROUND,
    EXECUTIVE_CONTEXT,
    CORE_AREAS_HEADING,
    CORE_AREAS,
    METHODOLOGY,
    EXPECTED_OUTCOME,
    ENGAGEMENT_STRUCTURE,
    PROFESSIONAL_ENGAGEMENT,
    CLOSING_NOTE,
    CONTACT,
} from "./data/brochure";

// Use built-in Helvetica (Helvetica, Helvetica-Bold) — no remote fetch needed.
const FONT = "Helvetica";
const FONT_BOLD = "Helvetica-Bold";

const INK = "#0b1a2e";
const TEXT = "#2d3540";
const MUTED = "#6b7280";
const RULE = "#d8d8d4";

const styles = StyleSheet.create({
    page: {
        paddingTop: 60,
        paddingBottom: 60,
        paddingHorizontal: 60,
        fontFamily: FONT,
        fontSize: 10.5,
        lineHeight: 1.55,
        color: TEXT,
        backgroundColor: "#ffffff",
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 30,
    },
    logoText: {
        marginLeft: 10,
        fontSize: 11,
        fontFamily: FONT_BOLD,
        color: INK,
        letterSpacing: 0.2,
    },
    footer: {
        position: "absolute",
        left: 60,
        right: 60,
        bottom: 30,
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 8,
        color: MUTED,
        borderTopWidth: 0.5,
        borderTopColor: RULE,
        paddingTop: 10,
    },
    coverWrap: {
        flex: 1,
        justifyContent: "space-between",
    },
    coverTop: {
        flexDirection: "row",
        alignItems: "center",
    },
    coverHero: {
        marginTop: 200,
    },
    coverEyebrow: {
        fontSize: 9,
        letterSpacing: 2.4,
        textTransform: "uppercase",
        color: INK,
        fontFamily: FONT_BOLD,
        marginBottom: 14,
    },
    coverTitle: {
        fontSize: 34,
        fontFamily: FONT_BOLD,
        color: INK,
        lineHeight: 1.1,
        letterSpacing: -0.5,
    },
    coverSubtitle: {
        marginTop: 18,
        fontSize: 13,
        color: TEXT,
        lineHeight: 1.5,
    },
    coverFootInfo: {
        flexDirection: "row",
        justifyContent: "space-between",
        borderTopWidth: 0.5,
        borderTopColor: RULE,
        paddingTop: 16,
    },
    coverFootCol: {
        flex: 1,
    },
    label: {
        fontSize: 8,
        color: MUTED,
        textTransform: "uppercase",
        letterSpacing: 1.8,
        marginBottom: 4,
    },
    valueText: {
        fontSize: 10,
        color: INK,
        fontFamily: FONT_BOLD,
    },

    h1: {
        fontSize: 22,
        fontFamily: FONT_BOLD,
        color: INK,
        letterSpacing: -0.3,
        marginBottom: 4,
    },
    h2: {
        fontSize: 14,
        fontFamily: FONT_BOLD,
        color: INK,
        letterSpacing: -0.1,
        marginTop: 18,
        marginBottom: 8,
    },
    h3: {
        fontSize: 11,
        fontFamily: FONT_BOLD,
        color: INK,
        marginTop: 12,
        marginBottom: 6,
        textTransform: "uppercase",
        letterSpacing: 1.2,
    },
    sectionNum: {
        fontSize: 9,
        color: MUTED,
        letterSpacing: 2,
        textTransform: "uppercase",
        marginBottom: 6,
    },
    rule: {
        height: 0.5,
        backgroundColor: RULE,
        marginVertical: 14,
    },
    para: {
        marginBottom: 8,
        textAlign: "justify",
    },
    bulletRow: {
        flexDirection: "row",
        marginBottom: 4,
    },
    bulletDot: {
        width: 10,
        fontSize: 10.5,
        color: INK,
    },
    bulletText: {
        flex: 1,
        fontSize: 10.5,
    },
    areaBlock: {
        marginTop: 18,
    },
    areaTitle: {
        flexDirection: "row",
        alignItems: "baseline",
        marginBottom: 6,
    },
    areaNum: {
        fontSize: 13,
        color: INK,
        fontFamily: FONT_BOLD,
        marginRight: 8,
    },
    areaHeading: {
        fontSize: 13,
        color: INK,
        fontFamily: FONT_BOLD,
        flex: 1,
        letterSpacing: -0.1,
    },
    twoCol: {
        flexDirection: "row",
        marginTop: 6,
    },
    col: {
        flex: 1,
        paddingRight: 16,
    },
    pageHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 24,
        borderBottomWidth: 0.5,
        borderBottomColor: RULE,
        paddingBottom: 10,
    },
    pageHeaderTitle: {
        fontSize: 9,
        color: INK,
        letterSpacing: 1.8,
        textTransform: "uppercase",
        fontFamily: FONT_BOLD,
    },
    pageHeaderLogo: {
        flexDirection: "row",
        alignItems: "center",
    },
});

const LogoSVG = ({ size = 22 }) => (
    <Svg width={size} height={size} viewBox="0 0 48 48">
        <Circle cx="24" cy="24" r="20.5" stroke={INK} strokeWidth="1.4" fill="none" />
        <Line x1="3.5" y1="24" x2="44.5" y2="24" stroke={INK} strokeWidth="1.1" />
        <Line x1="24" y1="3.5" x2="24" y2="44.5" stroke={INK} strokeWidth="1.1" />
        <Ellipse cx="24" cy="24" rx="10" ry="20.5" stroke={INK} strokeWidth="1.1" fill="none" />
        <Ellipse cx="24" cy="24" rx="20.5" ry="10" stroke={INK} strokeWidth="1.1" fill="none" />
    </Svg>
);

const PageHeader = ({ section }) => (
    <View style={styles.pageHeader} fixed>
        <View style={styles.pageHeaderLogo}>
            <LogoSVG size={18} />
            <Text style={[styles.logoText, { fontSize: 9 }]}>
                Nador Sage International
            </Text>
        </View>
        <Text style={styles.pageHeaderTitle}>{section}</Text>
    </View>
);

const PageFooter = () => (
    <View style={styles.footer} fixed>
        <Text>Nador Sage International S.r.l.</Text>
        <Text
            render={({ pageNumber, totalPages }) =>
                `${pageNumber} / ${totalPages}`
            }
        />
    </View>
);

const Bullet = ({ children }) => (
    <View style={styles.bulletRow}>
        <Text style={styles.bulletDot}>•</Text>
        <Text style={styles.bulletText}>{children}</Text>
    </View>
);

const Section = ({ title, num, children }) => (
    <View wrap>
        {num ? <Text style={styles.sectionNum}>{num}</Text> : null}
        <Text style={styles.h1}>{title}</Text>
        <View style={styles.rule} />
        {children}
    </View>
);

export const BrochureDocument = () => (
    <Document
        title="Nador Sage International — Strategic Reassessment & Operational Transformation"
        author="Nador Sage International S.r.l."
    >
        {/* COVER PAGE */}
        <Page size="A4" style={styles.page}>
            <View style={styles.coverWrap}>
                <View style={styles.coverTop}>
                    <LogoSVG size={32} />
                    <Text style={styles.logoText}>Nador Sage International</Text>
                </View>

                <View style={styles.coverHero}>
                    <Text style={styles.coverEyebrow}>
                        {HEADER.subtitle}
                    </Text>
                    <Text style={styles.coverTitle}>{HEADER.title}</Text>
                </View>

                <View style={styles.coverFootInfo}>
                    <View style={styles.coverFootCol}>
                        <Text style={styles.label}>Company</Text>
                        <Text style={styles.valueText}>{CONTACT.company}</Text>
                    </View>
                    <View style={styles.coverFootCol}>
                        <Text style={styles.label}>Contact</Text>
                        {CONTACT.emails.map((e) => (
                            <Text key={e} style={styles.valueText}>
                                {e}
                            </Text>
                        ))}
                    </View>
                    <View style={styles.coverFootCol}>
                        <Text style={styles.label}>Document</Text>
                        <Text style={styles.valueText}>Confidential</Text>
                    </View>
                </View>
            </View>
        </Page>

        {/* FIRM BACKGROUND */}
        <Page size="A4" style={styles.page}>
            <PageHeader section="Firm Background" />
            <Section title={FIRM_BACKGROUND.heading} num="01">
                {FIRM_BACKGROUND.paragraphs.map((p, i) => (
                    <Text key={i} style={styles.para}>
                        {p}
                    </Text>
                ))}

                <Text style={styles.h3}>
                    {FIRM_BACKGROUND.certificationsHeading}
                </Text>
                {FIRM_BACKGROUND.certifications.map((c, i) => (
                    <Bullet key={i}>{c}</Bullet>
                ))}
            </Section>
            <PageFooter />
        </Page>

        {/* EXECUTIVE CONTEXT */}
        <Page size="A4" style={styles.page}>
            <PageHeader section="Executive Context" />
            <Section title={EXECUTIVE_CONTEXT.heading} num="02">
                {EXECUTIVE_CONTEXT.paragraphs.map((p, i) => (
                    <Text key={i} style={styles.para}>
                        {p}
                    </Text>
                ))}
            </Section>
            <PageFooter />
        </Page>

        {/* CORE AREAS - intro + areas */}
        <Page size="A4" style={styles.page} wrap>
            <PageHeader section="Core Areas of Assessment & Repositioning" />
            <Text style={styles.sectionNum}>03</Text>
            <Text style={styles.h1}>{CORE_AREAS_HEADING}</Text>
            <View style={styles.rule} />

            {CORE_AREAS.map((a, idx) => (
                <View key={a.n} style={styles.areaBlock} wrap={false}>
                    <View style={styles.areaTitle}>
                        <Text style={styles.areaNum}>{a.n}</Text>
                        <Text style={styles.areaHeading}>{a.title}</Text>
                    </View>
                    <Text style={[styles.para, { marginTop: 2 }]}>
                        {a.intro}
                    </Text>

                    <View style={styles.twoCol}>
                        <View style={styles.col}>
                            <Text style={styles.h3}>Focus Areas</Text>
                            {a.focus.map((f, i) => (
                                <Bullet key={i}>{f}</Bullet>
                            ))}
                        </View>
                        <View style={styles.col}>
                            <Text style={styles.h3}>Deliverables</Text>
                            {a.deliverables.map((d, i) => (
                                <Bullet key={i}>{d}</Bullet>
                            ))}
                        </View>
                    </View>
                </View>
            ))}
            <PageFooter />
        </Page>

        {/* METHODOLOGY + EXPECTED OUTCOME */}
        <Page size="A4" style={styles.page}>
            <PageHeader section="Methodology & Expected Outcome" />
            <Section title={METHODOLOGY.heading} num="04">
                <Text style={styles.para}>{METHODOLOGY.intro}</Text>
                {METHODOLOGY.items.map((m, i) => (
                    <Bullet key={i}>{m}</Bullet>
                ))}
            </Section>

            <View style={{ marginTop: 28 }}>
                <Section title={EXPECTED_OUTCOME.heading} num="05">
                    <Text style={styles.para}>{EXPECTED_OUTCOME.intro}</Text>
                    {EXPECTED_OUTCOME.items.map((m, i) => (
                        <Bullet key={i}>{m}</Bullet>
                    ))}
                </Section>
            </View>
            <PageFooter />
        </Page>

        {/* ENGAGEMENT STRUCTURE + PROFESSIONAL ENGAGEMENT + CLOSING NOTE */}
        <Page size="A4" style={styles.page}>
            <PageHeader section="Engagement & Closing" />
            <Section title={ENGAGEMENT_STRUCTURE.heading} num="06">
                <Text style={styles.h3}>{ENGAGEMENT_STRUCTURE.subheading}</Text>
                {ENGAGEMENT_STRUCTURE.items.map((m, i) => (
                    <Bullet key={i}>{m}</Bullet>
                ))}
            </Section>

            <View style={{ marginTop: 28 }}>
                <Section title={PROFESSIONAL_ENGAGEMENT.heading} num="07">
                    <Text style={styles.para}>{PROFESSIONAL_ENGAGEMENT.text}</Text>
                </Section>
            </View>

            <View style={{ marginTop: 28 }}>
                <Section title={CLOSING_NOTE.heading} num="08">
                    <Text style={styles.para}>{CLOSING_NOTE.text}</Text>
                </Section>
            </View>

            <View style={{ marginTop: 36, borderTopWidth: 0.5, borderTopColor: RULE, paddingTop: 16 }}>
                <Text style={styles.label}>Contact</Text>
                <Text style={[styles.valueText, { marginBottom: 2 }]}>{CONTACT.company}</Text>
                {CONTACT.emails.map((e) => (
                    <Text key={e} style={[styles.valueText, { color: INK }]}>
                        {e}
                    </Text>
                ))}
            </View>
            <PageFooter />
        </Page>
    </Document>
);

/* Helper: trigger download */
export const downloadBrochurePdf = async () => {
    const blob = await pdf(<BrochureDocument />).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Nador-Sage-International-Brochure.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default BrochureDocument;
